import DealsAPI from 'dashboard/api/pipelines/deals';

const GLOBAL_CONTEXT_KEY = 'global';

const normalizeDeals = data => {
  if (Array.isArray(data)) return data;
  return data?.data || [];
};

const normalizeComparableValue = value => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? String(value) : parsed;
};

const normalizeContextParams = params => {
  const source = params || {};
  return Object.keys(source)
    .sort()
    .reduce((acc, key) => {
      const value = source[key];
      if (value === undefined || value === null || value === '') return acc;
      if (Array.isArray(value)) {
        const normalizedList = value
          .map(item => normalizeComparableValue(item))
          .filter(item => item !== null);
        if (!normalizedList.length) return acc;
        acc[key] = normalizedList;
        return acc;
      }
      acc[key] = normalizeComparableValue(value);
      return acc;
    }, {});
};

export const buildDealsContextKey = ({
  accountId = null,
  pipelineId = null,
  params = {},
} = {}) => {
  const normalizedAccountId = normalizeComparableValue(accountId);
  const normalizedPipelineId = normalizeComparableValue(pipelineId);
  const normalizedParams = normalizeContextParams(params);

  const serializedParams = Object.keys(normalizedParams)
    .map(key => {
      const value = normalizedParams[key];
      if (Array.isArray(value)) return `${key}=${value.join(',')}`;
      return `${key}=${value}`;
    })
    .join('&');

  return [
    `account:${normalizedAccountId ?? 'any'}`,
    `pipeline:${normalizedPipelineId ?? 'any'}`,
    `params:${serializedParams || 'none'}`,
  ].join('|');
};

const sortDeals = deals => {
  return [...deals].sort((a, b) => {
    const aPos = Number(a?.position);
    const bPos = Number(b?.position);
    const safeAPos = Number.isNaN(aPos) ? Number.MAX_SAFE_INTEGER : aPos;
    const safeBPos = Number.isNaN(bPos) ? Number.MAX_SAFE_INTEGER : bPos;
    if (safeAPos !== safeBPos) return safeAPos - safeBPos;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

const dealMatchesContext = (deal, contextMeta = {}) => {
  if (!deal) return false;

  const accountId = normalizeComparableValue(contextMeta.accountId);
  if (accountId !== null) {
    const dealAccountId = normalizeComparableValue(deal.account_id);
    if (dealAccountId !== null && dealAccountId !== accountId) {
      return false;
    }
  }

  const normalizedParams = normalizeContextParams(contextMeta.params);
  const pipelineFromParams = normalizeComparableValue(normalizedParams.pipeline_id);
  const pipelineFromContext = normalizeComparableValue(contextMeta.pipelineId);
  const expectedPipelineId =
    pipelineFromParams !== null ? pipelineFromParams : pipelineFromContext;

  if (
    expectedPipelineId !== null &&
    normalizeComparableValue(deal.pipeline_id) !== expectedPipelineId
  ) {
    return false;
  }

  if (normalizedParams.contact_id !== undefined) {
    if (
      normalizeComparableValue(deal.contact_id) !==
      normalizeComparableValue(normalizedParams.contact_id)
    ) {
      return false;
    }
  }

  if (normalizedParams.conversation_id !== undefined) {
    const expectedConversationId = normalizeComparableValue(
      normalizedParams.conversation_id
    );
    const conversationDisplayId = normalizeComparableValue(
      deal.conversation_display_id
    );
    const conversationId = normalizeComparableValue(deal.conversation_id);
    if (
      expectedConversationId !== conversationDisplayId &&
      expectedConversationId !== conversationId
    ) {
      return false;
    }
  }

  if (normalizedParams.pipeline_stage_id !== undefined) {
    if (
      normalizeComparableValue(deal.pipeline_stage_id) !==
      normalizeComparableValue(normalizedParams.pipeline_stage_id)
    ) {
      return false;
    }
  }

  if (normalizedParams.status !== undefined) {
    if (String(deal.status || '') !== String(normalizedParams.status || '')) {
      return false;
    }
  }

  return true;
};

const removeDealFromContextIndexes = (_state, dealId) => {
  Object.keys(_state.contextIndexes).forEach(contextKey => {
    _state.contextIndexes[contextKey] = (_state.contextIndexes[contextKey] || []).filter(
      id => Number(id) !== Number(dealId)
    );
  });
};

const reindexDealAcrossContexts = (_state, deal) => {
  if (!deal?.id) return;
  const dealId = Number(deal.id);

  Object.entries(_state.contextMeta).forEach(([contextKey, meta]) => {
    const ids = _state.contextIndexes[contextKey] || [];
    const hasDeal = ids.some(id => Number(id) === dealId);
    const shouldInclude = dealMatchesContext(deal, meta);

    if (shouldInclude && !hasDeal) {
      _state.contextIndexes[contextKey] = [...ids, dealId];
      return;
    }

    if (!shouldInclude && hasDeal) {
      _state.contextIndexes[contextKey] = ids.filter(id => Number(id) !== dealId);
    }
  });
};

const upsertDealRecord = (_state, data) => {
  const deal = data?.data || data;
  if (!deal?.id) return;
  const dealId = Number(deal.id);
  const current = _state.records[dealId] || {};
  const nextDeal = { ...current, ...deal };
  _state.records = { ..._state.records, [dealId]: nextDeal };
  reindexDealAcrossContexts(_state, nextDeal);
};

const setDealsForContextRecord = (_state, payload = {}) => {
  const contextKey = payload?.contextKey || GLOBAL_CONTEXT_KEY;
  const accountId = payload?.accountId || null;
  const pipelineId = payload?.pipelineId || null;
  const params = payload?.params || {};
  const deals = normalizeDeals(payload?.data);

  const ids = [];
  deals.forEach(item => {
    if (!item?.id) return;
    const dealId = Number(item.id);
    _state.records = { ..._state.records, [dealId]: item };
    ids.push(dealId);
  });

  _state.contextMeta = {
    ..._state.contextMeta,
    [contextKey]: { accountId, pipelineId, params },
  };
  _state.contextIndexes = {
    ..._state.contextIndexes,
    [contextKey]: ids,
  };

  if (contextKey === GLOBAL_CONTEXT_KEY) return;
  if (!_state.contextIndexes[GLOBAL_CONTEXT_KEY]?.length) {
    _state.contextIndexes[GLOBAL_CONTEXT_KEY] = ids;
    _state.contextMeta[GLOBAL_CONTEXT_KEY] = { accountId, pipelineId, params };
  }
};

const state = {
  records: {},
  contextIndexes: {
    [GLOBAL_CONTEXT_KEY]: [],
  },
  contextMeta: {
    [GLOBAL_CONTEXT_KEY]: {},
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isMoving: false,
  },
};

export const getters = {
  getDeals(_state) {
    return sortDeals(Object.values(_state.records));
  },
  getDealsByContext: _state => context => {
    const contextKey =
      typeof context === 'string'
        ? context
        : buildDealsContextKey(context || {});
    const ids = _state.contextIndexes[contextKey] || [];
    const deals = ids.map(id => _state.records[id]).filter(Boolean);
    return sortDeals(deals);
  },
  getDeal: _state => id => {
    return _state.records[id] || {};
  },
  getDealsByStage: _state => stageId => {
    return Object.values(_state.records)
      .filter(deal => deal.pipeline_stage_id === stageId)
      .sort((a, b) => a.position - b.position);
  },
  getDealsByPipeline: _state => pipelineId => {
    return Object.values(_state.records)
      .filter(deal => deal.pipeline_id === pipelineId)
      .sort((a, b) => a.position - b.position);
  },
  getDealsByStatus: _state => status => {
    return Object.values(_state.records)
      .filter(deal => deal.status === status)
      .sort((a, b) => a.position - b.position);
  },
  getOpenDeals(_state) {
    return Object.values(_state.records)
      .filter(deal => deal.status === 'open')
      .sort((a, b) => a.position - b.position);
  },
  getTotalAmountByStage: _state => stageId => {
    return Object.values(_state.records)
      .filter(deal => deal.pipeline_stage_id === stageId && deal.status === 'open')
      .reduce((sum, deal) => sum + parseFloat(deal.amount || 0), 0);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, { accountId, pipelineId = null, params = {} }) => {
    commit('SET_DEALS_UI_FLAG', { isFetching: true });
    try {
      const response = await DealsAPI.get(accountId, pipelineId, params);
      const contextKey = buildDealsContextKey({ accountId, pipelineId, params });
      commit('SET_DEALS_FOR_CONTEXT', {
        contextKey,
        accountId,
        pipelineId,
        params,
        data: response.data,
      });
    } finally {
      commit('SET_DEALS_UI_FLAG', { isFetching: false });
    }
  },

  show: async ({ commit }, { accountId, dealId }) => {
    commit('SET_DEALS_UI_FLAG', { isFetching: true });
    try {
      const response = await DealsAPI.show(accountId, dealId);
      commit('UPSERT_DEAL', response.data);
      return response.data;
    } finally {
      commit('SET_DEALS_UI_FLAG', { isFetching: false });
    }
  },

  create: async ({ commit }, { accountId, deal }) => {
    commit('SET_DEALS_UI_FLAG', { isCreating: true });
    try {
      const response = await DealsAPI.create(accountId, deal);
      commit('UPSERT_DEAL', response.data);
      return response.data;
    } finally {
      commit('SET_DEALS_UI_FLAG', { isCreating: false });
    }
  },

  update: async ({ commit }, { accountId, dealId, deal }) => {
    commit('SET_DEALS_UI_FLAG', { isUpdating: true });
    try {
      const response = await DealsAPI.update(accountId, dealId, { deal });
      commit('UPSERT_DEAL', response.data);
      return response.data;
    } finally {
      commit('SET_DEALS_UI_FLAG', { isUpdating: false });
    }
  },

  delete: async ({ commit }, { accountId, dealId }) => {
    commit('SET_DEALS_UI_FLAG', { isDeleting: true });
    try {
      await DealsAPI.delete(accountId, dealId);
      commit('DELETE_DEAL', dealId);
    } finally {
      commit('SET_DEALS_UI_FLAG', { isDeleting: false });
    }
  },

  move: async ({ commit }, { accountId, dealId, targetStageId }) => {
    commit('SET_DEALS_UI_FLAG', { isUpdating: true });
    try {
      const response = await DealsAPI.move(accountId, dealId, targetStageId);
      commit('UPSERT_DEAL', response.data);
      return response.data;
    } finally {
      commit('SET_DEALS_UI_FLAG', { isUpdating: false });
    }
  },

  filterByStatus: async ({ dispatch }, { accountId, status }) => {
    return dispatch('get', { accountId, params: { status } });
  },

  filterByStage: async ({ dispatch }, { accountId, pipelineStageId }) => {
    return dispatch('get', {
      accountId,
      params: { pipeline_stage_id: pipelineStageId },
    });
  },

  // ========================================
  // REALTIME ACTIONS
  // ========================================
  addDeal({ commit }, deal) {
    commit('UPSERT_DEAL', deal);
  },

  // Alias dispatched by ActionCable onDealCreated handler (injected via step 80)
  createDeal({ commit }, deal) {
    commit('UPSERT_DEAL', deal);
  },

  updateDeal({ commit }, deal) {
    commit('UPDATE_DEAL', deal);
  },

  moveDeal({ commit }, data) {
    commit('MOVE_DEAL', data);
  },

  deleteDeal({ commit }, dealId) {
    commit('DELETE_DEAL', dealId);
  },
};

export const mutations = {
  SET_DEALS_UI_FLAG(_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  SET_DEALS_FOR_CONTEXT(_state, payload) {
    setDealsForContextRecord(_state, payload);
  },

  SET_DEALS(_state, data) {
    setDealsForContextRecord(_state, {
      contextKey: GLOBAL_CONTEXT_KEY,
      data,
    });
  },

  ADD_DEAL(_state, data) {
    upsertDealRecord(_state, data);
  },

  EDIT_DEAL(_state, data) {
    upsertDealRecord(_state, data);
  },

  UPSERT_DEAL(_state, data) {
    upsertDealRecord(_state, data);
  },

  UPDATE_DEAL(_state, dealPayload) {
    upsertDealRecord(_state, dealPayload);
  },

  MOVE_DEAL(_state, payload) {
    upsertDealRecord(_state, payload);
  },

  DELETE_DEAL(_state, payload) {
    const id = payload?.id || payload?.deal_id || payload;
    const { [id]: _, ...rest } = _state.records;
    _state.records = rest;
    removeDealFromContextIndexes(_state, id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
